// ** Modules
import React from 'react';
import Script from 'next/script';
import NProgress from 'nprogress';
import { Router } from 'next/router';
import { Settings } from 'luxon';
import { addAPIProvider } from '@iconify/react';
import { ApolloProvider } from '@apollo/client/react';
import { SessionProvider } from 'next-auth/react';
import { GoogleAnalytics } from '@next/third-parties/google';
import { appWithTranslation } from 'next-i18next/pages';
import { CacheProvider, type EmotionCache } from '@emotion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ** Config
import nextI18NextConfig from '../../next-i18next.config.js';

// ** Theme
import themeConfig from '@/theme/themeConfig';

// ** Styles
import 'swiper/css';
import '@/styles/global.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-perfect-scrollbar/dist/css/styles.css';

// ** Utils
import { useGetApolloClient } from '@/lib/apollo-client';
import { createEmotionCache } from '@/utils/ui/styleUtils';

// ** Components
import ThemeComponent from '@/components/ThemeComponent';
import FirebaseInitializer from '@/components/FirebaseInitializer';
import { SettingsConsumer, SettingsProvider } from '@/components/SettingContext';

// ** Layout
import UserLayout from '@/components/layout/UserLayout';

// ** Types
import type { Session } from 'next-auth';
import type { AppProps } from 'next/app';
import type { Provider } from 'next-auth/providers';
import type { NextPageWithLayout } from '@/types/page';
import type { Settings as ContextSettings } from '@/types/contextTypes';

type AppPropsWithLayout = AppProps & {
	isMobile: boolean;
	Component: NextPageWithLayout;
	emotionCache?: EmotionCache;
	session: Session | null;
	providers: Provider;
};

const clientSideEmotionCache = createEmotionCache();

// Query Client Setup
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false
		}
	}
});

// Progress Bar Setup (NProgress)
if (themeConfig.routingLoader) {
	Router.events.on('routeChangeStart', () => NProgress.start());
	Router.events.on('routeChangeError', () => NProgress.done());
	Router.events.on('routeChangeComplete', () => NProgress.done());
}

// Iconify Settings
const MY_ICON_SERVER = process.env.NEXT_PUBLIC_ICONIFY_SERVER_URL || 'https://icons.tischler.studio';

addAPIProvider('', {
	resources: [MY_ICON_SERVER],
	rotate: 0
});

const MyApp = ({ Component, emotionCache = clientSideEmotionCache, pageProps }: AppPropsWithLayout) => {
	// 1. Get the current hostname (Client-side)
	const [gaId, setGaId] = React.useState('G-DEFAULT-ID');
	const [hasConsent, setHasConsent] = React.useState(false);

	React.useEffect(() => {
		const host = window.location.hostname;
		const currentGaId = host.includes('tischler.nrw') ? 'G-MSPWSJWVSD' : 'G-7S56GXR0D4';
		setGaId(currentGaId);

		const check = () => {
			const ccm = (window as any).CCM;
			if (ccm?.acceptedEmbeddings) {
				const accepted = ccm.acceptedEmbeddings;
				const isAccepted = accepted.some((item: any) => (item.name || '').toLowerCase().includes('google analytics'));

				setHasConsent(isAccepted);

				if (isAccepted && (window as any).gtag) {
					(window as any).gtag('consent', 'update', {
						analytics_storage: 'granted'
					});
				}
			}
		};

		// Listen for every possible CCM19 lifecycle event
		window.addEventListener('ccm19.ready', check);
		window.addEventListener('ccm19.consent.changed', check);
		window.addEventListener('ccm19.consentSet', check); // Some versions use this

		const interval = setInterval(check, 1000);

		return () => {
			window.removeEventListener('ccm19.ready', check);
			window.removeEventListener('ccm19.consent.changed', check);
			window.removeEventListener('ccm19.consentSet', check);
			clearInterval(interval);
		};
	}, []);

	// Log the app version
	// console.log(`App Version: ${process.env.NEXT_PUBLIC_APP_VERSION}`);

	// Variables
	const getLayout = Component.getLayout ?? ((page) => <UserLayout {...pageProps}>{page}</UserLayout>);
	const setConfig = Component.setConfig ?? undefined;

	// NextAuth Session
	const { session } = pageProps;

	// Luxon Settings
	Settings.defaultZone = 'Europe/Berlin';

	// const apolloClientState = apolloClient;
	const apolloClient = useGetApolloClient(pageProps);

	// Call setConfig if it exists
	let pageSettings: ContextSettings | undefined;

	if (setConfig) {
		const result = setConfig();
		if (result !== undefined) pageSettings = result;
	}

	return (
		<CacheProvider value={emotionCache}>
			{/* Next-Auth Provider */}
			<SessionProvider session={session} refetchInterval={10 * 60} refetchOnWindowFocus={true}>
				{/* Apollo Provider */}
				<ApolloProvider client={apolloClient}>
					{/* Tanstack Query Provider */}
					<QueryClientProvider client={queryClient}>
						{/* General Context Provider */}
						<SettingsProvider {...(pageSettings ? { pageSettings } : {})}>
							<SettingsConsumer>
								{({ settings }) => {
									return (
										// Theme Provider
										<ThemeComponent settings={settings}>
											<Script id="google-consent-mode" strategy="beforeInteractive">
												{`
													window.dataLayer = window.dataLayer || [];
													function gtag(){dataLayer.push(arguments);}
													gtag('consent', 'default', {
														'ad_storage': 'denied',
														'analytics_storage': 'denied',
														'ad_user_data': 'denied',
														'ad_personalization': 'denied'
													});
												`}
											</Script>
											<Script
												src="https://cloud.ccm19.de/app.js?apiKey=dac077d9084f9bb5bb0ba74471a78066644fefbec66e6a28&domain=63ff907b1f2975569fcd1aac"
												strategy="afterInteractive"
												// referrerPolicy="origin"
											/>
											<FirebaseInitializer />
											{getLayout(<Component {...pageProps} />)}
											{hasConsent && <GoogleAnalytics key={gaId} gaId={gaId} />}
										</ThemeComponent>
									);
								}}
							</SettingsConsumer>
						</SettingsProvider>
					</QueryClientProvider>
				</ApolloProvider>
			</SessionProvider>
		</CacheProvider>
	);
};

export default appWithTranslation(MyApp, nextI18NextConfig as any);
